import { Signer, providers } from "ethers"
import { Contract } from "ethers"
import { networkConfig } from "./network-config"

import { Publication } from "./types/contracts/Publication"
import { KnowledgeToken } from "./types/contracts/KnowledgeToken"
import { KnowledgeToken__factory } from "./types/factories/contracts"
import { Publication__factory } from "./types/factories/contracts"
import { URLS } from "./chains"

export const getKnowledge = (signer?: Signer, chainId?: number) => {
    if (!signer || !chainId || !networkConfig[chainId]) return undefined
    return new Contract(
        networkConfig[chainId].knowledge,
        KnowledgeToken__factory.abi,
        signer
    ) as KnowledgeToken
}

export const getPublication = (signer?: Signer, chainId?: number) => {
    if (!signer || !chainId || !networkConfig[chainId]) return undefined
    return new Contract(
        networkConfig[chainId].publication,
        Publication__factory.abi,
        signer
    ) as Publication
}

export const getNwPublication = (chainId?: number) => {
    if (!chainId || !networkConfig[chainId]) return undefined

    const nft = new Contract(
        networkConfig[chainId].publication,
        Publication__factory.abi,
        nwProvider(chainId)
    )

    return nft as Publication
}

export const getNwKnowledge = (chainId?: number) => {
    if (!chainId || !networkConfig[chainId]) return undefined
    const nft = new Contract(
        networkConfig[chainId].publication,
        KnowledgeToken__factory.abi,
        nwProvider(chainId)
    )

    return nft as KnowledgeToken
}

export const nwProvider = (chainId?: number) => {
    if (!chainId) return undefined
    return makeFallbackProvider(chainId)
}

export const nwProvider_ = (chainId: number) => {
    return makeFallbackProvider(chainId)
}

const CACHE_KEY = (chainId: number) => `rpc:working:${chainId}`

const storage = (): { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void } | undefined => {
    const g = globalThis as any
    return g?.sessionStorage
}

const getCachedWorkingUrl = (chainId: number): string | undefined => {
    try {
        return storage()?.getItem(CACHE_KEY(chainId)) ?? undefined
    } catch {
        return undefined
    }
}

const setCachedWorkingUrl = (chainId: number, url: string) => {
    try {
        storage()?.setItem(CACHE_KEY(chainId), url)
    } catch {
        // ignore
    }
}

const clearCachedWorkingUrl = (chainId: number) => {
    try {
        storage()?.removeItem(CACHE_KEY(chainId))
    } catch {
        // ignore
    }
}

const runtimeUrlOverride: { [chainId: number]: string[] } = {}

export const setRuntimeRpcUrls = (chainId: number, urls: string[]) => {
    if (urls.length > 0) {
        runtimeUrlOverride[chainId] = urls
        clearCachedWorkingUrl(chainId)
    }
}

const orderedUrls = (chainId: number): string[] => {
    const baseUrls = runtimeUrlOverride[chainId] ?? URLS[chainId] ?? []
    const cached = getCachedWorkingUrl(chainId)
    if (cached && baseUrls.includes(cached)) {
        return [cached, ...baseUrls.filter((u) => u !== cached)]
    }
    return baseUrls
}

// Wraps StaticJsonRpcProvider so each request tries the URL list in order,
// caching whichever one responded. ethereum-multicall and ethers contracts
// only need .send / .perform / .detectNetwork to work.
class RotatingProvider extends providers.StaticJsonRpcProvider {
    private readonly chainId_: number

    constructor(chainId: number) {
        const urls = orderedUrls(chainId)
        const first = urls[0]
        if (!first) {
            throw new Error(`No RPC URLs configured for chain ${chainId}`)
        }
        super(first, chainId)
        this.chainId_ = chainId
    }

    async send(method: string, params: Array<any>): Promise<any> {
        const urls = orderedUrls(this.chainId_)
        let lastErr: any
        for (const url of urls) {
            try {
                const p = new providers.StaticJsonRpcProvider(url, this.chainId_)
                const result = await p.send(method, params)
                setCachedWorkingUrl(this.chainId_, url)
                return result
            } catch (e) {
                lastErr = e
                // try next url
                if (getCachedWorkingUrl(this.chainId_) === url) {
                    clearCachedWorkingUrl(this.chainId_)
                }
            }
        }
        throw lastErr
    }
}

export const makeFallbackProvider = (chainId: number) => {
    return new RotatingProvider(chainId)
}
